import { useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import UIkit from "uikit";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import classnames from "classnames";

import { Form } from "../../../../ui";
import Tags from "../../../common/Tags";
import {
  ITEM_CATEGORY_OPTIONS,
  TRACKING_MODE_OPTIONS,
  UNIT_OPTIONS,
  labelFor,
} from "../data";

const schema = yup.object().shape({
  name: yup.string().required("Item name is required"),
  sku: yup.string().required("SKU / code is required"),
  category: yup.string().required("Category is required"),
  unit: yup.string().required("Unit is required"),
  tracking_mode: yup.string().required("Tracking mode is required"),
  unit_cost: yup
    .number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable()
    .min(0, "Unit cost must be 0 or more"),
  replacement_cost: yup
    .number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable()
    .min(0, "Replacement cost must be 0 or more"),
  minimum_quantity: yup
    .number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable()
    .min(0, "Minimum quantity must be 0 or more"),
  reorder_quantity: yup
    .number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable()
    .min(0, "Reorder quantity must be 0 or more"),
});

const defaultValues = {
  name: "",
  sku: "",
  category: "linen",
  unit: "each",
  pack_size: 1,
  tracking_mode: "consumable",
  default_reorder_point: "",
  default_par_level: "",
  minimum_quantity: "",
  reorder_quantity: "",
  preferred_supplier: "",
  unit_cost: "",
  replacement_cost: "",
  tags: [],
  notes: "",
  active: true,
};

const StorageItemAdd = ({ item, flagIsNew, flagIsProcessing, onSave, onCancel }) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
    clearErrors,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const active = watch("active");
  const category = watch("category");
  const unit = watch("unit");
  const trackingMode = watch("tracking_mode");
  const tags = watch("tags") || [];

  const resetForm = useCallback(() => {
    Object.keys(defaultValues).forEach((field) => {
      setValue(field, defaultValues[field]);
    });
  }, [setValue]);

  useEffect(() => {
    clearErrors();
    const source = item || {};

    Object.keys(defaultValues).forEach((field) => {
      setValue(field, source[field] !== undefined ? source[field] : defaultValues[field]);
    });

    if (!item) resetForm();
  }, [item, clearErrors, setValue, resetForm]);

  const handleRegister = useCallback(
    async (formdata) => {
      const payload = {
        ...formdata,
        id: item?.id,
        pack_size: Number(formdata.pack_size) || 1,
        default_reorder_point: formdata.default_reorder_point
          ? Number(formdata.default_reorder_point)
          : null,
        default_par_level: formdata.default_par_level
          ? Number(formdata.default_par_level)
          : null,
        minimum_quantity: formdata.minimum_quantity
          ? Number(formdata.minimum_quantity)
          : null,
        reorder_quantity: formdata.reorder_quantity
          ? Number(formdata.reorder_quantity)
          : null,
        unit_cost: formdata.unit_cost ? Number(formdata.unit_cost) : null,
        replacement_cost: formdata.replacement_cost
          ? Number(formdata.replacement_cost)
          : null,
        active: !!formdata.active,
      };

      try {
        const result = await (onSave ? onSave(payload) : true);
        if (result !== false) {
          UIkit.offcanvas("#modal-storage-item-add").hide();
          reset(defaultValues);
        }
      } catch (err) {
        UIkit.notification("Failed to save item.", "error");
      }
    },
    [item, onSave, reset],
  );

  const handleInvalid = useCallback(() => {
    UIkit.notification("Complete the required item fields.", "warning");
  }, []);

  const handleClose = useCallback(() => {
    if (onCancel) onCancel();
    UIkit.offcanvas("#modal-storage-item-add").hide();
  }, [onCancel]);

  return (
    <div
      id="modal-storage-item-add"
      className="uk-modal-full hms-modals uk-modal uk-open uk-dark"
      uk-offcanvas="flip: true; overlay: true"
    >
      <div className="uk-offcanvas-bar">
        <div className="hms-modals-main">
          <button
            className="uk-offcanvas-close uk-close-large"
            type="button"
            uk-close="true"
            onClick={handleClose}
          ></button>

          <div className="uk-modal-header">
            <h2 className="uk-modal-title">{flagIsNew ? "Add Item" : "Edit Item"}</h2>
          </div>

          <div className="uk-modal-body">
            <div className="add-contact-form">
              <Form onSubmit={handleSubmit(handleRegister, handleInvalid)} data-uk-grid="true">
                <div className="uk-width-1-1">
                  <label className="uk-form-label">Item Name*</label>
                  <input
                    className={classnames("uk-input", { "uk-form-danger": errors.name })}
                    type="text"
                    {...register("name")}
                  />
                  {errors.name && <div className="uk-text-danger uk-text-small">{errors.name.message}</div>}
                </div>

                <div className="uk-width-1-2@s">
                  <label className="uk-form-label">SKU / Code*</label>
                  <input
                    className={classnames("uk-input", { "uk-form-danger": errors.sku })}
                    type="text"
                    {...register("sku")}
                  />
                  {errors.sku && <div className="uk-text-danger uk-text-small">{errors.sku.message}</div>}
                </div>

                <div className="uk-width-1-2@s">
                  <label className="uk-form-label">Category*</label>
                  <div uk-form-custom="target: > * > span:first-child">
                    <select {...register("category")}>
                      {ITEM_CATEGORY_OPTIONS.filter((option) => option.value).map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <button className="uk-button uk-button-default" type="button" tabIndex="-1">
                      <span className="select-value">
                        {labelFor(ITEM_CATEGORY_OPTIONS, category, "Select Category")}
                      </span>
                      <span uk-icon="icon: chevron-down" className="hms-align-right"></span>
                    </button>
                  </div>
                  {errors.category && <div className="uk-text-danger uk-text-small">{errors.category.message}</div>}
                </div>

                <div className="uk-width-1-2@s">
                  <label className="uk-form-label">Storage Unit*</label>
                  <div uk-form-custom="target: > * > span:first-child">
                    <select {...register("unit")}>
                      {UNIT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <button className="uk-button uk-button-default" type="button" tabIndex="-1">
                      <span className="select-value">{labelFor(UNIT_OPTIONS, unit)}</span>
                      <span uk-icon="icon: chevron-down" className="hms-align-right"></span>
                    </button>
                  </div>
                  {errors.unit && <div className="uk-text-danger uk-text-small">{errors.unit.message}</div>}
                </div>

                <div className="uk-width-1-2@s">
                  <label className="uk-form-label">Pack Size</label>
                  <input className="uk-input" type="number" min="1" {...register("pack_size")} />
                </div>

                <div className="uk-width-1-2@s">
                  <label className="uk-form-label">Tracking Mode*</label>
                  <div uk-form-custom="target: > * > span:first-child">
                    <select {...register("tracking_mode")}>
                      {TRACKING_MODE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <button className="uk-button uk-button-default" type="button" tabIndex="-1">
                      <span className="select-value">
                        {labelFor(TRACKING_MODE_OPTIONS, trackingMode)}
                      </span>
                      <span uk-icon="icon: chevron-down" className="hms-align-right"></span>
                    </button>
                  </div>
                  {errors.tracking_mode && (
                    <div className="uk-text-danger uk-text-small">{errors.tracking_mode.message}</div>
                  )}
                </div>

                <div className="uk-width-1-2@s">
                  <label className="uk-form-label">Minimum Quantity</label>
                  <input
                    className={classnames("uk-input", { "uk-form-danger": errors.minimum_quantity })}
                    type="number"
                    min="0"
                    step="1"
                    {...register("minimum_quantity")}
                  />
                  {errors.minimum_quantity && (
                    <div className="uk-text-danger uk-text-small">{errors.minimum_quantity.message}</div>
                  )}
                </div>

                <div className="uk-width-1-2@s">
                  <label className="uk-form-label">Reorder Quantity</label>
                  <input
                    className={classnames("uk-input", { "uk-form-danger": errors.reorder_quantity })}
                    type="number"
                    min="0"
                    step="1"
                    {...register("reorder_quantity")}
                  />
                  {errors.reorder_quantity && (
                    <div className="uk-text-danger uk-text-small">{errors.reorder_quantity.message}</div>
                  )}
                </div>

                <div className="uk-width-1-2@s">
                  <label className="uk-form-label">Default Reorder Point</label>
                  <input className="uk-input" type="number" min="0" {...register("default_reorder_point")} />
                </div>

                <div className="uk-width-1-2@s">
                  <label className="uk-form-label">Default Par Level</label>
                  <input className="uk-input" type="number" min="0" {...register("default_par_level")} />
                </div>

                <div className="uk-width-1-2@s">
                  <label className="uk-form-label">Preferred Supplier</label>
                  <input className="uk-input" type="text" {...register("preferred_supplier")} />
                </div>

                <div className="uk-width-1-2@s">
                  <label className="uk-form-label">Unit Cost</label>
                  <input
                    className={classnames("uk-input", { "uk-form-danger": errors.unit_cost })}
                    type="number"
                    min="0"
                    step="0.01"
                    {...register("unit_cost")}
                  />
                  {errors.unit_cost && <div className="uk-text-danger uk-text-small">{errors.unit_cost.message}</div>}
                </div>

                <div className="uk-width-1-2@s">
                  <label className="uk-form-label">Replacement Cost</label>
                  <input
                    className={classnames("uk-input", { "uk-form-danger": errors.replacement_cost })}
                    type="number"
                    min="0"
                    step="0.01"
                    {...register("replacement_cost")}
                  />
                  {errors.replacement_cost && (
                    <div className="uk-text-danger uk-text-small">{errors.replacement_cost.message}</div>
                  )}
                </div>

                <div className="uk-width-1-1 hms-add-tag">
                  <label className="uk-form-label">Tags</label>
                  <Tags value={tags} onChange={(editedTags) => setValue("tags", editedTags)} />
                </div>

                <div className="uk-width-1-1">
                  <label className="uk-form-label">Notes</label>
                  <textarea className="uk-textarea" rows="2" {...register("notes")} />
                </div>

                <div className="uk-width-1-1 uk-text-left">
                  <label className="uk-form-label">Status</label>
                  <div className="autosend-checkbox uk-flex uk-flex-left uk-flex-middle">
                    <label className="setting-switch">
                      <input
                        type="checkbox"
                        checked={!!active}
                        onChange={(event) => setValue("active", event.target.checked)}
                      />
                      <span className="setting-slider setting-round"></span>
                    </label>
                    <span className="uk-margin-small-left">{active ? "Active" : "Inactive"}</span>
                  </div>
                </div>

                <div className="uk-width-1-1 uk-margin-top">
                  <button className="uk-button hms-btn hms-red-btn" type="submit">
                    {flagIsProcessing ? <div data-uk-spinner="ratio: 0.5;"></div> : "Save"}
                  </button>
                  <button
                    className="uk-button hms-btn uk-offcanvas-close uk-margin-small-left"
                    onClick={handleClose}
                    type="button"
                  >
                    Cancel
                  </button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StorageItemAdd;
